import { CALENDAR, MAREAS_PER_CAMPAIGN, WAVES_PER_MAREA } from '@somnhold/content';

/**
 * Campaign calendar (SPEC §8.1). The simulation never sees a clock: this module turns the group's
 * time zone and wave hours into UTC instants, and the server feeds "which wave is due" to the sim.
 */

const DAY = 86_400_000;

/** One formatter per zone: making one is the slow part, and the bastion asks for the wave's hour every frame. */
const FORMATS = new Map<string, Intl.DateTimeFormat>();
/** The latest answers (the same instants come back frame after frame); cleared whole when it fills. */
const OFFSETS = new Map<string, number>();

/** Offset (ms) of `tz` from UTC at instant `utc`. */
export const tzOffset = (utc: number, tz: string): number => {
  const key = `${tz}|${utc}`;
  const known = OFFSETS.get(key);
  if (known !== undefined) return known;
  let f = FORMATS.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    FORMATS.set(tz, f);
  }
  const parts: Record<string, string> = {};
  for (const p of f.formatToParts(new Date(utc))) parts[p.type] = p.value;
  const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second));
  const off = asUtc - Math.floor(utc / 1000) * 1000;
  if (OFFSETS.size >= 512) OFFSETS.clear();
  OFFSETS.set(key, off);
  return off;
};

/** Local calendar date (y, m, d) of an instant in a zone. */
export const localDate = (utc: number, tz: string): { y: number; m: number; d: number } => {
  const local = new Date(utc + tzOffset(utc, tz));
  return { y: local.getUTCFullYear(), m: local.getUTCMonth(), d: local.getUTCDate() };
};

/** UTC instant of a local wall-clock time in a zone (DST-safe for whole hours). */
export const zonedToUtc = (y: number, m: number, d: number, hour: number, tz: string): number => {
  const guess = Date.UTC(y, m, d, hour);
  let utc = guess - tzOffset(guess, tz);
  // Second pass fixes the offset when the guess and the answer straddle a DST change.
  utc = guess - tzOffset(utc, tz);
  return utc;
};

export interface CalendarSpec {
  createdAt: number;
  timezone: string;
  waveHours: [number, number, number];
  /** Dev-only accelerated pace: one campaign "day" lasts this many ms. */
  dayMs?: number;
  /** A fixed pace set for one campaign: wave n of the campaign falls at firstAt + n × everyMs. */
  pace?: { firstAt: number; everyMs: number };
}

/** The group's three wave hours are valid: in order through the day and CALENDAR.minWaveGapHours apart. */
export const waveHoursValid = (h: readonly number[]): boolean =>
  h.length === 3 && h.every((x, i) => i === 0 || x - (h[i - 1] as number) >= CALENDAR.minWaveGapHours);

/** Instant of wave `w` (0..5) of calendar Marea `m` (0, 1, …; a Marea played again takes the next slot). Day 0 is the creation day in the group's zone. */
export const waveAt = (c: CalendarSpec, m: number, w: number): number => {
  if (c.pace) return c.pace.firstAt + (m * WAVES_PER_MAREA + w) * c.pace.everyMs;
  const day = CALENDAR.mareaEveryDays * m + CALENDAR.firstMareaDay + Math.floor(w / 3);
  const hour = c.waveHours[w % 3] as number;
  if (c.dayMs) return c.createdAt + day * c.dayMs + Math.floor((hour * c.dayMs) / 24);
  const d0 = localDate(c.createdAt, c.timezone);
  return zonedToUtc(d0.y, d0.m, d0.d + day, hour, c.timezone);
};

/** Campaign day of an instant (day 0 = creation day, days change at the group's midnight). */
export const campaignDay = (c: CalendarSpec, now: number): number => {
  if (c.dayMs) return Math.floor((now - c.createdAt) / c.dayMs);
  const a = localDate(c.createdAt, c.timezone);
  const b = localDate(now, c.timezone);
  return Math.round((Date.UTC(b.y, b.m, b.d) - Date.UTC(a.y, a.m, a.d)) / DAY);
};

/** How long before a wave its "adelanto" can be played. */
export const previewWindow = (c: CalendarSpec): number =>
  c.pace ? Math.min(CALENDAR.previewWindowMs, Math.floor(c.pace.everyMs / 2)) : c.dayMs ? Math.floor(c.dayMs / 12) : CALENDAR.previewWindowMs;

export const omenAt = (c: CalendarSpec, m: number): number =>
  waveAt(c, m, 0) - (c.pace ? Math.floor(c.pace.everyMs / 2) : c.dayMs ? Math.floor((CALENDAR.omenHoursBefore * c.dayMs) / 24) : CALENDAR.omenHoursBefore * 3_600_000);

export type Phase = 'calm' | 'omen' | 'marea' | 'over';

export interface Moment {
  phase: Phase;
  marea: number;
  wave: number;
  /** The calendar slot of the Marea (its index, shifted by the Mareas played again): what the clock reads. */
  slot: number;
  /** Next wave instant (or 0 when the campaign is over). */
  next: number;
}

/**
 * Where the calendar stands at `now`, given the next unresolved wave, its calendar slot (`slot`: the Marea's
 * index shifted by the Mareas played again) and whether the campaign still runs (the tide never ends on its own).
 */
export const momentAt = (c: CalendarSpec, marea: number, wave: number, now: number, active = true, slot = marea): Moment => {
  if (!active) return { phase: 'over', marea, wave, slot, next: 0 };
  const next = waveAt(c, slot, wave);
  let phase: Phase = 'calm';
  if (wave > 0) phase = 'marea';
  else if (now >= omenAt(c, slot)) phase = 'omen';
  return { phase, marea, wave, slot, next };
};

/** The wave instants of the first season (for schedules and tests). */
export const allWaves = (c: CalendarSpec): { marea: number; wave: number; at: number }[] => {
  const out: { marea: number; wave: number; at: number }[] = [];
  for (let m = 0; m < MAREAS_PER_CAMPAIGN; m++) for (let w = 0; w < WAVES_PER_MAREA; w++) out.push({ marea: m, wave: w, at: waveAt(c, m, w) });
  return out;
};

export { DAY };
